import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "../ui/Button";

import { splitRowCount } from "../../lib/caption-wrap";
import {
  isUnplottableSeries,
  seriesRequestFromChart,
  specFromSeries,
} from "../../lib/dashboard-chart";
import { getApi } from "../../lib/get-api";
import { previewCacheToken, previewIdFromLocation } from "../../lib/preview-as";
import { Field } from "../../lib/schema-field";
import { useAuth } from "../../lib/useAuth";
import { ChartFigure } from "./ChartFigure";
import { DashboardCard } from "./DashboardCard";

interface ChartCardProps {
  datasourceId: string;
  name: string;
  title?: string;
  description?: string;
  type?: string;
  /** Column the generator chose. Absent on rows written before it was stored. */
  groupBy?: string;
  /** "month" for a time series. */
  bucket?: string;
  /**
   * Column to SUM. Only what the API stored: with no measure the aggregate
   * counts records, which is what a card titled "by <group>" claims. Guessing
   * one made the plot contradict its own title.
   */
  measure?: string;
  collection?: string;
  /**
   * Same JSON string the collection list uses. Sent to the aggregate only
   * when non-empty. Dashboard charts omit it.
   */
  filterGroups?: string;
  schema: Field[];
  /** Grid placement from the dashboard, e.g. spanning both columns. */
  className?: string;
  /**
   * Called with true once the series comes back unplottable. Plottability is
   * only known after this card's own query resolves, so the parent cannot
   * filter the list before laying the row out: the card has to report back.
   */
  onUnplottable?: (unplottable: boolean) => void;
}

const CaptionText: React.FC<{ text: string }> = ({ text }) => {
  const parts = splitRowCount(text);
  if (!parts) return <>{text}</>;
  return (
    <>
      {parts.before}
      <span className="whitespace-nowrap">{parts.count}</span>
      {parts.after}
    </>
  );
};

export const ChartCard: React.FC<ChartCardProps> = ({
  datasourceId,
  name,
  title,
  description,
  type,
  groupBy,
  bucket,
  measure,
  collection,
  filterGroups,
  schema,
  className,
  onUnplottable,
}) => {
  const token = useAuth((state) => state.token);
  const location = useLocation();
  const previewToken = previewCacheToken(previewIdFromLocation(location));
  const theme = useTheme();
  // Collection below sm is charts + search, not a table. The series toggle
  // still painted "Show table" on 390 with no grid to show (nbg1 recapture).
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [showTable, setShowTable] = useState(false);
  const tableOpen = showTable && !phone;

  // The generator's axis wins. It is the only thing that makes the card title
  // true: re-deriving from the schema returns the same first match for every
  // chart on the page, so a card titled "by priority" would plot status. The
  // measure is passed through for the same reason and never guessed.
  const request = useMemo(
    () => seriesRequestFromChart({ type, groupBy, bucket, measure }, schema),
    [type, groupBy, bucket, measure, schema],
  );

  // The list still reads the legacy `filters` param. That string is not
  // filterGroups JSON; sending it makes the aggregate 400 and the chart
  // read as broken. Only a JSON object is the list's current filter.
  let activeFilter: string | undefined;
  if (typeof filterGroups === "string" && filterGroups.length > 0) {
    try {
      const parsed = JSON.parse(filterGroups);
      if (parsed && typeof parsed === "object") activeFilter = filterGroups;
    } catch {
      activeFilter = undefined;
    }
  }

  const { data, isLoading, error } = useQuery({
    queryKey: activeFilter
      ? ["chartSeries", datasourceId, collection, request, activeFilter, previewToken]
      : ["chartSeries", datasourceId, collection, request, previewToken],
    queryFn: async () => {
      const api = getApi({ token });
      const response = await api.data.aggregateSeries(
        datasourceId,
        collection!,
        {
          groupBy: request!.groupBy,
          measure: request!.measure,
          bucket: request!.bucket,
          ...(activeFilter ? { filterGroups: activeFilter } : {}),
        },
      );
      return response.data;
    },
    // No listData fallback: pulling a capped page and aggregating it in the
    // browser is the bug this endpoint replaces, and a silent fallback would
    // put a 100-row chart back on screen without saying so.
    enabled: !!datasourceId && !!collection && !!request,
    staleTime: 60000,
  });

  const spec = useMemo(
    () => (data && request ? specFromSeries(data, request, schema) : null),
    [data, request, schema],
  );

  // A one-period time series has nothing to draw. It used to render a
  // half-width card containing only the "no trend to draw yet" sentence, which
  // sat beside the donut on the collection landing and read as a chart that had
  // failed to load. Report it up so the parent can drop the cell, then render
  // nothing.
  const unplottable = !!(
    collection &&
    request &&
    data &&
    isUnplottableSeries(request, data.points ?? [])
  );

  useEffect(() => {
    onUnplottable?.(unplottable);
  }, [unplottable, onUnplottable]);

  if (unplottable) return null;

  return (
    <DashboardCard
      className={className}
      title={title || name}
      description={description}
      action={
        spec && !phone ? (
          <Button
            onClick={() => setShowTable((open) => !open)}
            variant="secondary" size="sm"
          >
            {tableOpen ? "Show chart" : "Show table"}
          </Button>
        ) : undefined
      }
    >
      {!collection ? (
        <p className="m-0 text-xs text-od-muted">
          This chart does not name a collection, so there is nothing to plot.
        </p>
      ) : isLoading ? (
        <p className="m-0 text-xs text-od-muted">Loading…</p>
      ) : error ? (
        <p className="m-0 text-xs text-od-danger">
          Could not read {collection}.
        </p>
      ) : !request || !spec ? (
        <p className="m-0 text-xs text-od-muted">
          No column in <code className="text-od-text">{collection}</code> groups
          into a readable chart. Charts need a column with a small set of
          repeated values.
        </p>
      ) : (
        <>
          <p className="m-0 mb-3 text-balance text-xs text-od-muted">
            <CaptionText text={spec.caption} />
          </p>
          {tableOpen ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-od-muted">
                  <th className="border-b border-od-border py-1 font-medium">
                    Group
                  </th>
                  <th className="border-b border-od-border py-1 text-right font-medium">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {spec.points.map((point) => (
                  <tr key={point.label}>
                    <td className="border-b border-od-border py-1 text-od-text">
                      {point.label}
                    </td>
                    <td
                      className="border-b border-od-border py-1 text-right text-od-text"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {point.value.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <ChartFigure spec={spec} />
          )}
        </>
      )}
    </DashboardCard>
  );
};

export default ChartCard;
